"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useTranslation } from "react-i18next";
import { api, type FeedEventRow } from "@/lib/api";

/**
 * EPIC-046 part 1 — chronological activity timeline.
 *
 * Two source modes:
 *   { handle }              → `/v1/users/:handle/activity` (public; per-handle)
 *   { source: "following" } → `/v1/me/feed` (authed; followee union)
 *
 * Cursor-paginated; "Load more" appends the next page. Each row
 * branches on `eventType` for its summary line + deep-link. Unknown
 * event types fall through to a neutral "[type] activity" placeholder
 * so a future producer that lands before this renderer is updated
 * still renders something — it just won't have a custom label yet.
 */
type ActivityTimelineProps =
  | { handle: string }
  | { source: "following" };

export function ActivityTimeline(props: ActivityTimelineProps) {
  const isFollowing = "source" in props && props.source === "following";
  const handle = "handle" in props ? props.handle : null;
  const sourceKey = isFollowing ? "__following__" : (handle ?? "");

  const { t } = useTranslation();
  const [events, setEvents] = useState<FeedEventRow[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialLoaded, setInitialLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true);
    const promise = isFollowing
      ? api.listMyFollowingFeed({ limit: 20 })
      : api.listUserActivity(handle ?? "", { limit: 20 });
    void promise
      .then((res) => {
        if (cancelled) return;
        setEvents(res.events);
        setNextCursor(res.nextCursor);
        setInitialLoaded(true);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load.");
          setInitialLoaded(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceKey]);

  async function loadMore() {
    if (!nextCursor || loading) return;
    setLoading(true);
    try {
      const res = isFollowing
        ? await api.listMyFollowingFeed({
            cursor: nextCursor,
            limit: 20,
          })
        : await api.listUserActivity(handle ?? "", {
            cursor: nextCursor,
            limit: 20,
          });
      setEvents((prev) => [...prev, ...res.events]);
      setNextCursor(res.nextCursor);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load.");
    } finally {
      setLoading(false);
    }
  }

  if (!initialLoaded && loading) {
    return (
      <p className="text-[12px] text-[var(--color-text3)]">
        {t("activityTimeline.loading")}
      </p>
    );
  }
  if (error) {
    return (
      <p
        role="alert"
        className="text-[12px]"
        style={{ color: "var(--color-no)" }}
      >
        {error}
      </p>
    );
  }
  if (events.length === 0) {
    return (
      <p className="text-[12px] text-[var(--color-text3)]">
        {t("activityTimeline.empty")}
      </p>
    );
  }

  return (
    <ol className="space-y-2">
      {events.map((e) => (
        <li
          key={e.id}
          className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2"
        >
          <ActivityRow event={e} />
        </li>
      ))}
      {nextCursor && (
        <li className="text-center">
          <button
            type="button"
            onClick={loadMore}
            disabled={loading}
            className="rounded-md border border-[var(--color-border)] px-3 py-1 text-[11px] font-bold text-[var(--color-text2)] hover:border-[var(--color-border2)] hover:text-[var(--color-text)] disabled:opacity-50"
          >
            {loading
              ? t("activityTimeline.loadingMore")
              : t("activityTimeline.loadMore")}
          </button>
        </li>
      )}
    </ol>
  );
}

function ActivityRow({ event }: { event: FeedEventRow }) {
  const date = new Date(event.occurredAt).toLocaleString();
  const username = event.authorUsername;
  const displayName = event.authorDisplayName ?? username ?? "Deleted user";
  const initial = displayName.charAt(0).toUpperCase();
  return (
    <div className="flex items-start gap-3">
      {/* Author avatar — small circle. Link out to the profile when
          we have a username; render a generic placeholder if the
          author was soft-deleted between the event and this read. */}
      <AuthorAvatar
        username={username}
        initial={initial}
        avatarUrl={event.authorAvatarUrl}
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <div className="min-w-0 truncate text-[12px] text-[var(--color-text2)]">
            {username ? (
              <Link
                href={`/u/${encodeURIComponent(username)}`}
                className="font-semibold text-[var(--color-text)] hover:underline"
              >
                {displayName}
              </Link>
            ) : (
              <span className="font-semibold text-[var(--color-text)]">
                {displayName}
              </span>
            )}
            {username ? (
              <span className="ml-1 text-[var(--color-text3)]">
                @{username}
              </span>
            ) : null}
          </div>
          <time
            dateTime={event.occurredAt}
            className="flex-shrink-0 text-[10px] text-[var(--color-text3)]"
          >
            {date}
          </time>
        </div>
        <div className="mt-0.5 text-[12px] text-[var(--color-text)]">
          <ActivitySummary event={event} />
        </div>
      </div>
    </div>
  );
}

function AuthorAvatar(props: {
  username: string | null;
  initial: string;
  avatarUrl: string | null;
}) {
  const inner = props.avatarUrl ? (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={props.avatarUrl}
      alt=""
      className="h-7 w-7 rounded-full object-cover"
    />
  ) : (
    <div
      className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-bold"
      style={{
        background:
          "color-mix(in srgb, var(--color-text3) 14%, transparent)",
        color: "var(--color-text2)",
      }}
      aria-hidden="true"
    >
      {props.initial}
    </div>
  );
  if (!props.username) {
    return <div className="flex-shrink-0">{inner}</div>;
  }
  return (
    <Link
      href={`/u/${encodeURIComponent(props.username)}`}
      className="flex-shrink-0"
      aria-label={`Open ${props.username}'s profile`}
    >
      {inner}
    </Link>
  );
}

function ActivitySummary({ event }: { event: FeedEventRow }) {
  const p = event.payload as Record<string, unknown>;
  switch (event.eventType) {
    case "post.authored": {
      const title = typeof p.title === "string" ? p.title : null;
      const preview =
        typeof p.body_preview === "string" ? p.body_preview : null;
      return (
        <span>
          📝 Posted{" "}
          {event.sourceId ? (
            <Link
              href={`/posts/${event.sourceId}`}
              className="text-[var(--color-brand)] hover:underline"
            >
              {title || preview || "a post"}
            </Link>
          ) : (
            title || preview || "a post"
          )}
        </span>
      );
    }
    case "post.liked": {
      const postId =
        typeof p.post_id === "string" ? p.post_id : event.sourceId;
      const postTitle =
        typeof p.post_title === "string" && p.post_title.length > 0
          ? p.post_title
          : "a post";
      return (
        <span>
          ❤️ Liked{" "}
          {postId ? (
            <Link
              href={`/posts/${postId}`}
              className="text-[var(--color-brand)] hover:underline"
            >
              {postTitle}
            </Link>
          ) : (
            postTitle
          )}
        </span>
      );
    }
    case "comment.authored":
    case "comment.replied": {
      const verb =
        event.eventType === "comment.replied"
          ? "Replied to a comment"
          : "Commented";
      // Prefer market_slug for the deep-link (the /markets route is
      // keyed off slug, not UUID — back-fall to bare market_id only
      // for legacy events written before the producer started
      // emitting the slug).
      const marketSlug =
        typeof p.market_slug === "string" ? p.market_slug : null;
      const marketId =
        typeof p.market_id === "string" ? p.market_id : null;
      const marketHref = marketSlug ? `/markets/${marketSlug}` : null;
      const preview =
        typeof p.body_preview === "string" ? p.body_preview : null;
      return (
        <span>
          💬 {verb}
          {marketHref ? (
            <>
              {" — "}
              <Link
                href={marketHref}
                className="text-[var(--color-brand)] hover:underline"
              >
                view market
              </Link>
            </>
          ) : marketId ? (
            <span className="text-[var(--color-text3)]"> — view market</span>
          ) : null}
          {preview ? `: "${preview}"` : ""}
        </span>
      );
    }
    case "copy_subscription.created": {
      const piUsername =
        typeof p.pi_username === "string" ? p.pi_username : null;
      return (
        <span>
          🤝 Started copying{" "}
          {piUsername ? (
            <Link
              href={`/u/${encodeURIComponent(piUsername)}`}
              className="text-[var(--color-brand)] hover:underline"
            >
              @{piUsername}
            </Link>
          ) : (
            "a Popular Investor"
          )}
        </span>
      );
    }
    case "discussion.authored": {
      const topicSlug =
        typeof p.topic_slug === "string" ? p.topic_slug : null;
      const title =
        typeof p.discussion_title === "string" ? p.discussion_title : null;
      const preview =
        typeof p.body_preview === "string" ? p.body_preview : null;
      const href =
        topicSlug && event.sourceId
          ? `/topic/${encodeURIComponent(topicSlug)}/d/${event.sourceId}`
          : null;
      return (
        <span>
          🗣️ Started a discussion
          {topicSlug ? (
            <>
              {" in "}
              <Link
                href={`/topic/${encodeURIComponent(topicSlug)}`}
                className="text-[var(--color-brand)] hover:underline"
              >
                #{topicSlug}
              </Link>
            </>
          ) : null}
          {href && title ? (
            <>
              {": "}
              <Link
                href={href}
                className="text-[var(--color-brand)] hover:underline"
              >
                {title}
              </Link>
            </>
          ) : title ? (
            <>: {title}</>
          ) : preview ? (
            <>: &ldquo;{preview}&rdquo;</>
          ) : null}
        </span>
      );
    }
    case "trade.executed": {
      // EPIC-046 STORY-1102 payload (2026-05-19) carries side +
      // marketSlug + priceCents + sizeShares (centi-shares). Render
      // a meaningful summary instead of the prior "💱 Trade executed"
      // placeholder. Outcome name isn't in the payload (would need
      // an extra join per fill in the matcher tx); the market link
      // gives the user the context if they want to drill in.
      const side = typeof p.side === "string" ? p.side : null;
      const slug =
        typeof p.marketSlug === "string" ? p.marketSlug : null;
      const priceCents =
        typeof p.priceCents === "number" ? p.priceCents : null;
      // sizeShares is centi-shares (BIGINT). Render as whole shares
      // with up to 2 decimals — matches the lib/format.ts convention
      // for `formatShares`.
      const sizeRaw =
        typeof p.sizeShares === "string" || typeof p.sizeShares === "number"
          ? String(p.sizeShares)
          : null;
      const centiShares = sizeRaw ? Number(sizeRaw) : null;
      const wholeShares =
        centiShares !== null && Number.isFinite(centiShares)
          ? centiShares / 100
          : null;
      const sharesText =
        wholeShares !== null
          ? wholeShares % 1 === 0
            ? String(wholeShares)
            : wholeShares.toFixed(2)
          : null;
      const verb =
        side === "buy" ? "bought" : side === "sell" ? "sold" : "traded";
      const emoji = side === "buy" ? "🟢" : side === "sell" ? "🔴" : "💱";
      return (
        <span>
          {emoji} {verb}
          {sharesText ? ` ${sharesText} sh` : ""}
          {priceCents !== null ? ` @ ${priceCents}¢` : ""}
          {slug ? (
            <>
              {" "}
              in{" "}
              <Link
                href={`/markets/${encodeURIComponent(slug)}`}
                className="text-[var(--color-brand)] hover:underline"
              >
                {slug}
              </Link>
            </>
          ) : null}
        </span>
      );
    }
    case "market.resolved": {
      const slug =
        typeof p.market_slug === "string" ? p.market_slug : null;
      const title =
        typeof p.market_title === "string" ? p.market_title : null;
      const outcome =
        typeof p.outcome === "string" ? p.outcome : null;
      const verb =
        outcome === "won"
          ? "won on"
          : outcome === "refunded"
            ? "got refunded on"
            : outcome === "lost"
              ? "lost on"
              : "saw resolution of";
      const emoji =
        outcome === "won" ? "🏆" : outcome === "refunded" ? "↩️" : "🏁";
      return (
        <span>
          {emoji} {verb}{" "}
          {slug && title ? (
            <Link
              href={`/markets/${encodeURIComponent(slug)}`}
              className="text-[var(--color-brand)] hover:underline"
            >
              {title}
            </Link>
          ) : title ? (
            title
          ) : (
            "a market"
          )}
        </span>
      );
    }
    default:
      return <span>{event.eventType} activity</span>;
  }
}
